import React, { useRef, useState } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import CartLoader from "../../Helper/Loaders/CartLoader";
import "swiper/css";
import "swiper/css/navigation";
import "./KPICarousel.css";

/**
 * KPICarousel - A reusable carousel component for displaying KPI cards
 *
 * @param {Object} props
 * @param {Array} props.data - Array of KPI data objects to display
 * @param {Function} props.renderCard - Render function for each card, receives (item, index) as arguments
 * @param {boolean} props.loading - Loading state
 * @param {string} props.error - Error message if any
 * @param {string} props.className - Optional custom class name for the swiper
 * @param {number} props.slideWidth - Width of each slide (default: 320)
 * @param {number} props.spaceBetween - Space between slides (default: 18)
 * @param {number} props.autoWidthThreshold - Number of items below which auto-width is applied (default: 4)
 */
const KPICarousel = ({
    data = [],
    renderCard,
    loading = false,
    error = null,
    className = "kpi-carousel",
    slideWidth = 320,
    spaceBetween = 18,
    autoWidthThreshold = 4,
}) => {
    const swiperRef = useRef(null);
    const [isBeginning, setIsBeginning] = useState(true);
    const [isEnd, setIsEnd] = useState(false);
    const [showArrows, setShowArrows] = useState(false);

    const updateArrowVisibility = (swiper) => {
        if (!swiper?.el) return;
        const containerWidth = swiper.el.clientWidth;
        const totalSlides = data.length;
        const totalWidth = (slideWidth * totalSlides) + (spaceBetween * (totalSlides - 1));
        setShowArrows(totalWidth > containerWidth);
    };

    // Loading State
    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "200px" }}>
                <CartLoader />
            </Box>
        );
    }

    // Error State
    if (error) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "200px" }}>
                <Typography variant="fw400s16c000000" sx={{ color: "#d32f2f" }}>
                    Error loading KPI data: {error}
                </Typography>
            </Box>
        );
    }

    // Empty State
    if (data.length === 0) {
        return null;
    }

    return (
        <Box
            className={`${className}-nav-wrapper`}
            sx={{
                position: "relative",
                width: "100%",
                ...(showArrows && {
                    "&:hover .nav-button": {
                        opacity: 1,
                    },
                }),
            }}
        >
            {/* Custom Previous Button */}
            {showArrows && (
                <IconButton
                    className="nav-button"
                    onClick={() => swiperRef.current?.slidePrev()}
                    disabled={isBeginning}
                    sx={{
                        position: "absolute",
                        left: "-13px",
                        top: "85px",
                        zIndex: 10,
                        width: "28px",
                        height: "28px",
                        backgroundColor: "#F3F5F7",
                        border: "1px solid #BBBBBB",
                        opacity: 0,
                        transition: "opacity 0.3s ease",
                        "&:hover": {
                            backgroundColor: "#EFF0FF",
                            borderColor: '#8F93FF',
                            "& svg": {
                                color: "#6D72F6",
                            },
                        },
                        "&.Mui-disabled": {
                            backgroundColor: "#EAEAEA",
                            borderColor: "#EAEAEA",
                            "& svg": {
                                color: "#B2B2B2",
                            },
                        },
                    }}
                >
                    <ChevronLeft sx={{ fontSize: "24px", color: "#000000" }} />
                </IconButton>
            )}

            {/* Custom Next Button */}
            {showArrows && (
                <IconButton
                    className="nav-button"
                    onClick={() => swiperRef.current?.slideNext()}
                    disabled={isEnd}
                    sx={{
                        position: "absolute",
                        right: "-13px",
                        top: "85px",
                        zIndex: 10,
                        width: "28px",
                        height: "28px",
                        backgroundColor: "#F3F5F7",
                        border: "1px solid #BBBBBB",
                        opacity: 0,
                        transition: "opacity 0.3s ease",
                        "&:hover": {
                            backgroundColor: "#EFF0FF",
                            borderColor: '#8F93FF',
                            "& svg": {
                                color: "#6D72F6",
                            },
                        },
                        "&.Mui-disabled": {
                            backgroundColor: "#EAEAEA",
                            borderColor: "#EAEAEA",
                            "& svg": {
                                color: "#B2B2B2",
                            },
                        },
                    }}
                >
                    <ChevronRight sx={{ fontSize: "24px", color: "#000000" }} />
                </IconButton>
            )}

            <Box
                className={`${className}-container`}
                sx={{
                    overflow: "hidden",
                    width: "100%",
                }}
            >
                <Swiper
                    onSwiper={(swiper) => {
                        swiperRef.current = swiper;
                        setTimeout(() => updateArrowVisibility(swiper), 100);
                    }}
                    onSlideChange={(swiper) => {
                        setIsBeginning(swiper.isBeginning);
                        setIsEnd(swiper.isEnd);
                    }}
                    onResize={(swiper) => {
                        updateArrowVisibility(swiper);
                    }}
                    className={`${className}-swiper ${data.length <= autoWidthThreshold ? 'auto-width' : ''}`}
                    modules={[Navigation]}
                    spaceBetween={spaceBetween}
                    slidesPerView="auto"
                    allowTouchMove={data.length > autoWidthThreshold}
                >
                    {data.map((item, index) => (
                        <SwiperSlide key={index}>
                            {renderCard(item, index)}
                        </SwiperSlide>
                    ))}
                </Swiper>
            </Box>
        </Box>
    );
};

export default KPICarousel;
